import React from 'react';
import PropTypes from 'prop-types';
import styles from './StatGrid.module.css';

/**
 * Container component for displaying StatCards in a responsive grid
 *
 * Automatically adjusts columns based on screen size:
 * - Desktop: up to 4 columns
 * - Tablet: 2 columns
 * - Mobile: 1 column
 *
 * Example:
 * <StatGrid>
 *   <StatCard number="142" label="Total Users" />
 *   <StatCard number="24" label="Pending" variant="warning" />
 *   <StatCard number="8" label="Active" variant="success" />
 * </StatGrid>
 *
 * Custom columns:
 * <StatGrid columns={3}>
 *   {stats.map(stat => <StatCard key={stat.id} {...stat} />)}
 * </StatGrid>
 */
const StatGrid = ({
  children,
  columns,
  gap = 'medium',
  className = '',
  ...rest
}) => {
  const classNames = [
    styles.statGrid,
    columns && styles[`cols-${columns}`],
    styles[`gap-${gap}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} {...rest}>
      {children}
    </div>
  );
};

StatGrid.propTypes = {
  /** Child StatCard components */
  children: PropTypes.node.isRequired,

  /** Fixed number of columns (overrides responsive default) */
  columns: PropTypes.oneOf([1, 2, 3, 4]),

  /** Gap size between cards */
  gap: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Additional CSS class names */
  className: PropTypes.string,
};

export default StatGrid;
